// 3-step modal: (1) email + settings, (2) preview by zone with reassign, (3) send.
// Reads from /api/handoff/preview and writes to /api/handoff/send.

import React, { useMemo, useState } from 'react';
import { handoffApi, masterListAiApi } from './zonesApi';

const UNASSIGNED_KEY = '__unassigned';

export default function HandoffModal({
  projectId,
  planId = null,
  projectName,
  defaultHomeownerEmail = '',
  defaultHomeownerName = '',
  onClose,
}) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [error, setError] = useState(null);
  const [successInfo, setSuccessInfo] = useState(null);

  // Step 1
  const [homeownerEmail, setHomeownerEmail] = useState(defaultHomeownerEmail || '');
  const [homeownerName, setHomeownerName] = useState(defaultHomeownerName || '');
  const [hemisphere, setHemisphere] = useState('northern');
  const [locationLabel, setLocationLabel] = useState('');
  const [climateZone, setClimateZone] = useState('');
  const [message, setMessage] = useState('');

  // Step 2
  const [previewZones, setPreviewZones] = useState([]); // [{plan_zone_id, name, default_season, plants:[]}]
  const [unassigned, setUnassigned] = useState([]);
  const [excluded, setExcluded] = useState(new Set()); // master_list_id
  const [plantZoneOverrides, setPlantZoneOverrides] = useState({}); // master_list_id -> zoneKey (plan_zone_id or UNASSIGNED_KEY)
  const [totals, setTotals] = useState({ zones: 0, plants: 0, ai_filled: 0, missing_ai: 0 });

  async function loadPreview() {
    try {
      setLoading(true);
      setError(null);
      const r = await handoffApi.preview({ projectId, planId, homeownerEmail: homeownerEmail || null });
      setPreviewZones(r.zones || []);
      setUnassigned(r.unassigned || []);
      setTotals(r.totals || { zones: 0, plants: 0, ai_filled: 0, missing_ai: 0 });
      if (r.project?.settings) {
        if (!locationLabel && r.project.settings.location_label) setLocationLabel(r.project.settings.location_label);
        if (r.project.settings.hemisphere) setHemisphere(r.project.settings.hemisphere);
        if (!climateZone && r.project.settings.climate_zone) setClimateZone(r.project.settings.climate_zone);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  function validStep1() {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(homeownerEmail.trim());
  }

  async function goToStep2() {
    await loadPreview();
    setStep(2);
  }

  // Effective zone of a plant (taking overrides into account)
  function zoneKeyOf(plant, defaultKey) {
    if (!plant.master_list_id) return defaultKey;
    return plantZoneOverrides[plant.master_list_id] ?? defaultKey;
  }

  // Flatten all plants with their current effective zone key for the UI.
  const groupedPlants = useMemo(() => {
    const out = new Map();
    const byMaster = new Map();
    const ensure = (key, name, default_season, plan_zone_id = null, sun_exposure = null, site_notes = null) => {
      if (!out.has(key)) {
        out.set(key, {
          key,
          name,
          default_season,
          plan_zone_id,
          sun_exposure,
          site_notes,
          plants: [],
        });
      }
      return out.get(key);
    };

    for (const z of previewZones) {
      ensure(
        z.plan_zone_id,
        z.name,
        z.default_season || 'spring',
        z.plan_zone_id,
        z.sun_exposure ?? null,
        z.site_notes ?? null,
      );
    }
    ensure(UNASSIGNED_KEY, 'Unassigned', 'spring', null, null, null);

    const allPlants = [
      ...previewZones.flatMap((z) => (z.plants || []).map((p) => [p, z.plan_zone_id])),
      ...unassigned.map((p) => [p, UNASSIGNED_KEY]),
    ];
    for (const [p, defaultKey] of allPlants) {
      if (p.master_list_id) byMaster.set(p.master_list_id, p);
      const effectiveKey = zoneKeyOf(p, defaultKey);
      const target = out.get(effectiveKey) || out.get(UNASSIGNED_KEY);
      target.plants.push(p);
    }

    return { zones: Array.from(out.values()), byMaster };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewZones, unassigned, plantZoneOverrides]);

  function setOverride(masterListId, zoneKey) {
    setPlantZoneOverrides((prev) => ({ ...prev, [masterListId]: zoneKey }));
  }

  function toggleExcluded(masterListId) {
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(masterListId)) next.delete(masterListId);
      else next.add(masterListId);
      return next;
    });
  }

  async function handleAiFillMissing() {
    try {
      setAiBusy(true);
      setError(null);
      const missing = [];
      const walk = (p) => { if (!p.ai_filled && p.master_list_id) missing.push(p.master_list_id); };
      previewZones.forEach((z) => (z.plants || []).forEach(walk));
      unassigned.forEach(walk);
      if (!missing.length) return;
      await masterListAiApi.fillBulk(projectId, { itemIds: missing, onlyMissing: true });
      await loadPreview();
    } catch (e) {
      setError(e.message);
    } finally {
      setAiBusy(false);
    }
  }

  async function handleAiFillOne(masterListId) {
    try {
      setAiBusy(true);
      setError(null);
      await masterListAiApi.fillItem(projectId, masterListId);
      await loadPreview();
    } catch (e) {
      setError(e.message);
    } finally {
      setAiBusy(false);
    }
  }

  async function handleSend() {
    try {
      setSending(true);
      setError(null);

      // Build payload: regroup effective zones + keep unassigned bucket.
      const effectiveZones = [];
      const effectiveUnassigned = [];

      for (const group of groupedPlants.zones) {
        const plants = group.plants
          .filter((p) => !(p.master_list_id && excluded.has(p.master_list_id)))
          .map((p) => ({
            master_list_id: p.master_list_id,
            source_plant_id: p.source_plant_id,
            source_image: p.source_image,
            common_name: p.common_name,
            botanical_name: p.botanical_name,
            quantity: p.quantity,
            care: p.care || {},
            seasonal: p.seasonal || {},
            flags: p.flags || {},
            x_percent: p.x_percent,
            y_percent: p.y_percent,
            location_notes: p.location_notes,
          }));
        if (group.key === UNASSIGNED_KEY) {
          effectiveUnassigned.push(...plants);
        } else {
          effectiveZones.push({
            plan_zone_id: group.plan_zone_id,
            name: group.name,
            default_season: group.default_season || 'spring',
            sun_exposure: group.sun_exposure ?? null,
            site_notes: group.site_notes ?? null,
            plants,
          });
        }
      }

      const r = await handoffApi.send({
        projectId,
        planId,
        homeownerEmail: homeownerEmail.trim(),
        homeownerName: homeownerName.trim() || null,
        message: message.trim() || null,
        settings: {
          hemisphere,
          location_label: locationLabel.trim() || null,
          climate_zone: climateZone.trim() || null,
        },
        zones: effectiveZones,
        unassigned: effectiveUnassigned,
      });

      setSuccessInfo(r);
      setStep(3);
    } catch (e) {
      setError(e.message);
    } finally {
      setSending(false);
    }
  }

  const totalIncluded = useMemo(() => {
    let n = 0;
    for (const g of groupedPlants.zones) {
      for (const p of g.plants) if (!(p.master_list_id && excluded.has(p.master_list_id))) n += 1;
    }
    return n;
  }, [groupedPlants, excluded]);

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[95vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Send project to homeowner</h2>
            <p className="text-sm text-gray-600">
              {projectName ? projectName : 'Garden handoff'}
              {' · '}
              They get an email with a link to open their prepopulated Garden App.
            </p>
          </div>
          <button className="text-gray-400 hover:text-gray-700 text-xl" onClick={onClose}>×</button>
        </div>

        {/* Step indicator */}
        <div className="px-6 py-3 border-b flex items-center gap-4 text-sm">
          {[
            { n: 1, label: 'Details' },
            { n: 2, label: 'Plants & zones' },
            { n: 3, label: 'Send' },
          ].map((s) => (
            <div key={s.n} className={`flex items-center gap-2 ${step >= s.n ? 'text-emerald-700' : 'text-gray-400'}`}>
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${step >= s.n ? 'bg-emerald-600 text-white' : 'bg-gray-200'}`}>{s.n}</div>
              <span className="font-medium">{s.label}</span>
            </div>
          ))}
        </div>

        {error && (
          <div className="px-6 py-2 bg-red-50 text-red-700 text-sm border-b border-red-100">{error}</div>
        )}

        <div className="flex-1 overflow-auto">
          {step === 1 && (
            <div className="p-6 space-y-4 max-w-lg">
              <div className="rounded-lg border border-emerald-100 bg-emerald-50/80 px-3 py-3 text-sm text-gray-800 space-y-2">
                <p className="font-medium text-emerald-900">What happens next</p>
                <ul className="list-disc pl-4 space-y-1 text-gray-700">
                  <li>We email them a link to open their Garden App with your plants already in it.</li>
                  <li>Optional: use <strong>Zones</strong> on the project page to group plants by area on the drawing. If you skip zones, plants still send (often listed together).</li>
                </ul>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Homeowner email *</label>
                <input
                  type="email"
                  value={homeownerEmail}
                  onChange={(e) => setHomeownerEmail(e.target.value)}
                  className="w-full border rounded px-3 py-2 text-sm"
                  placeholder="jane@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Homeowner name (optional)</label>
                <input
                  type="text"
                  value={homeownerName}
                  onChange={(e) => setHomeownerName(e.target.value)}
                  className="w-full border rounded px-3 py-2 text-sm"
                  placeholder="Jane Smith"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Hemisphere</label>
                  <select
                    value={hemisphere}
                    onChange={(e) => setHemisphere(e.target.value)}
                    className="w-full border rounded px-3 py-2 text-sm"
                  >
                    <option value="northern">Northern</option>
                    <option value="southern">Southern</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Hardiness zone</label>
                  <input
                    type="text"
                    value={climateZone}
                    onChange={(e) => setClimateZone(e.target.value)}
                    className="w-full border rounded px-3 py-2 text-sm"
                    placeholder="e.g. 6a"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location label</label>
                <input
                  type="text"
                  value={locationLabel}
                  onChange={(e) => setLocationLabel(e.target.value)}
                  className="w-full border rounded px-3 py-2 text-sm"
                  placeholder="e.g. Portland, OR"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Personal note (optional)</label>
                <textarea
                  rows={3}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="w-full border rounded px-3 py-2 text-sm"
                  placeholder="Any notes to include in the email?"
                />
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="p-6 space-y-4">
              {loading && <div className="text-sm text-gray-500">Loading preview…</div>}
              {!loading && (
                <>
                  <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 text-sm text-gray-800 space-y-2">
                    <p>
                      This step is <strong>not</strong> the same as marking care reviewed on this project&apos;s <strong>Project Plant List</strong>. Here you choose what goes into their Garden App: which plants, which zone each plant belongs to, and whether to skip any plant.
                    </p>
                    <p>
                      If you have not drawn zones on the plan, most plants appear under <strong>Unassigned</strong>. That is normal. You can still send, or use <strong>Zones</strong> on the project page first if you want named areas in their app.
                    </p>
                    <ul className="list-disc pl-4 text-gray-700 space-y-0.5">
                      <li>Use the dropdown on each row to move a plant between zones.</li>
                      <li><strong>Exclude</strong> removes a plant from what they receive.</li>
                      <li><strong>AI fill</strong> adds missing care text where needed.</li>
                    </ul>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-600">
                      {totals.plants} plant{totals.plants === 1 ? '' : 's'} · {totals.zones} zone{totals.zones === 1 ? '' : 's'} drawn ·
                      {' '}<span className={totals.missing_ai ? 'text-amber-700' : 'text-emerald-700'}>
                        {totals.ai_filled}/{totals.plants} AI filled
                      </span>
                    </div>
                    <button
                      onClick={handleAiFillMissing}
                      disabled={aiBusy || totals.missing_ai === 0}
                      className="text-sm px-3 py-1.5 rounded border border-purple-300 text-purple-700 hover:bg-purple-50 disabled:opacity-50"
                    >
                      {aiBusy ? 'Filling…' : `✨ AI fill ${totals.missing_ai} missing`}
                    </button>
                  </div>

                  <div className="space-y-4">
                    {groupedPlants.zones.map((group) => (
                      <div key={group.key} className="border rounded-lg">
                        <div className="px-3 py-2 bg-gray-50 border-b flex items-center justify-between">
                          <div className="font-medium text-gray-900">{group.name}</div>
                          <div className="text-xs text-gray-500">{group.plants.length} plant{group.plants.length === 1 ? '' : 's'}</div>
                        </div>
                        <div className="divide-y">
                          {group.plants.length === 0 && (
                            <div className="px-3 py-3 text-sm text-gray-400 italic">No plants in this zone.</div>
                          )}
                          {group.plants.map((p) => {
                            const key = p.master_list_id || `${p.common_name}-${group.key}`;
                            const isExcluded = p.master_list_id && excluded.has(p.master_list_id);
                            return (
                              <div
                                key={key}
                                className={`flex items-center gap-3 px-3 py-2 ${isExcluded ? 'opacity-50' : ''}`}
                              >
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-medium text-gray-900 truncate">
                                    {p.common_name}
                                    {p.botanical_name ? <span className="text-gray-500 italic"> ({p.botanical_name})</span> : null}
                                    {p.quantity > 1 ? <span className="text-gray-500"> ×{p.quantity}</span> : null}
                                  </div>
                                  <div className="text-xs mt-0.5">
                                    {p.ai_filled ? (
                                      <span className="text-emerald-700">✓ AI care filled</span>
                                    ) : (
                                      <span className="text-amber-700">
                                        AI care missing
                                        {p.master_list_id && (
                                          <button
                                            onClick={() => handleAiFillOne(p.master_list_id)}
                                            disabled={aiBusy}
                                            className="ml-2 underline hover:text-amber-900 disabled:opacity-50"
                                          >fill now</button>
                                        )}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <select
                                  disabled={!p.master_list_id}
                                  value={zoneKeyOf(p, group.key)}
                                  onChange={(e) => setOverride(p.master_list_id, e.target.value)}
                                  className="text-xs border rounded px-2 py-1"
                                  title="Move to zone"
                                >
                                  {groupedPlants.zones.map((g) => (
                                    <option key={g.key} value={g.key}>{g.name}</option>
                                  ))}
                                </select>
                                {p.master_list_id && (
                                  <button
                                    onClick={() => toggleExcluded(p.master_list_id)}
                                    className={`text-xs px-2 py-1 rounded border ${isExcluded ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
                                    title={isExcluded ? 'Include' : 'Exclude from handoff'}
                                  >
                                    {isExcluded ? 'Include' : 'Exclude'}
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {step === 3 && successInfo && (
            <div className="p-6 space-y-4">
              <div className="p-4 border border-emerald-200 bg-emerald-50 rounded-lg">
                <div className="text-emerald-800 font-semibold">✔ Invite sent</div>
                <div className="text-sm text-emerald-900 mt-1">
                  We emailed <strong>{homeownerEmail}</strong> with a link to open their garden.
                </div>
                <div className="mt-3 text-xs text-emerald-900 break-all">
                  Link: <a className="underline" href={successInfo.handoff?.invite_url} target="_blank" rel="noreferrer">{successInfo.handoff?.invite_url}</a>
                </div>
              </div>
              <div className="text-sm text-gray-600">
                Once they accept, their garden will be prepopulated with <strong>{successInfo.handoff?.plant_count}</strong> plants across <strong>{successInfo.handoff?.zone_count}</strong> zones. You’ll keep co-edit access until they turn it off.
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between px-6 py-3 border-t bg-gray-50">
          <div className="text-xs text-gray-500">
            {step === 2 && (
              <>Sending {totalIncluded} plant{totalIncluded === 1 ? '' : 's'}.</>
            )}
          </div>
          <div className="flex items-center gap-2">
            {step === 1 && (
              <>
                <button className="px-4 py-2 text-sm rounded border" onClick={onClose}>Cancel</button>
                <button
                  className="px-4 py-2 text-sm rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                  onClick={goToStep2}
                  disabled={!validStep1() || loading}
                >{loading ? 'Loading…' : 'Continue to preview'}</button>
              </>
            )}
            {step === 2 && (
              <>
                <button className="px-4 py-2 text-sm rounded border" onClick={() => setStep(1)}>Back</button>
                <button
                  className="px-4 py-2 text-sm rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                  onClick={handleSend}
                  disabled={sending || totalIncluded === 0}
                >{sending ? 'Sending…' : `Send to ${homeownerEmail || 'homeowner'}`}</button>
              </>
            )}
            {step === 3 && (
              <button className="px-4 py-2 text-sm rounded bg-gray-800 text-white" onClick={onClose}>Close</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
